import { useMemo, useState } from 'react';
import { MapPin, Navigation, Search } from 'lucide-react';
import { ACTIVE_TRIPS, DELAY_STATUS } from './mockDispatchData';

const FILTER_TABS = [
  { id: 'all', label: 'All' },
  { id: 'delayed', label: 'Delayed' },
  { id: 'normal', label: 'On Schedule' },
];

function DelayBadge({ status }) {
  const config = DELAY_STATUS[status];
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ring-1 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

function ProgressBar({ pct, delayed }) {
  return (
    <div className="flex min-w-[120px] items-center gap-2">
      <div className="h-2 min-w-[72px] flex-1 overflow-hidden rounded-full bg-logi-silver/70">
        <div
          className={`h-full rounded-full transition-all ${delayed ? 'bg-red-500' : 'bg-cyan-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-9 shrink-0 text-right font-mono text-[10px] font-bold tabular-nums text-logi-text">
        {pct}%
      </span>
    </div>
  );
}

export default function LiveTripMonitorTable({
  trips = ACTIVE_TRIPS,
  selectedTripId,
  onSelectTrip,
}) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  const delayedCount = trips.filter((t) => t.delayStatus === 'delayed').length;
  const onScheduleCount = trips.length - delayedCount;

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return trips.filter((trip) => {
      const matchesFilter =
        filter === 'all' ||
        (filter === 'delayed' && trip.delayStatus === 'delayed') ||
        (filter === 'normal' && trip.delayStatus !== 'delayed');

      if (!matchesFilter) return false;
      if (!q) return true;

      return (
        trip.id.toLowerCase().includes(q) ||
        trip.vehicle.toLowerCase().includes(q) ||
        trip.driver.toLowerCase().includes(q) ||
        trip.route.toLowerCase().includes(q) ||
        trip.product.toLowerCase().includes(q)
      );
    });
  }, [trips, query, filter]);

  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-logi-grid bg-white shadow-lg">
      <div className="shrink-0 border-b border-logi-grid px-4 py-2.5 sm:px-5">
        <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-logi-accent/10 ring-1 ring-logi-accent/30">
              <Navigation className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-logi-text">Live Trip Monitor</h3>
              <p className="text-[10px] text-logi-muted">
                {trips.length} active · {onScheduleCount} on schedule · {delayedCount} delayed
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {FILTER_TABS.map((tab) => {
              const isActive = filter === tab.id;
              const count =
                tab.id === 'all'
                  ? trips.length
                  : tab.id === 'delayed'
                    ? delayedCount
                    : onScheduleCount;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setFilter(tab.id)}
                  className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide transition ${
                    isActive
                      ? 'bg-logi-accent/15 text-logi-accent ring-1 ring-logi-accent/30'
                      : 'bg-logi-silver/80 text-logi-muted ring-1 ring-logi-grid/60 hover:bg-logi-silver'
                  }`}
                >
                  {tab.label} ({count})
                </button>
              );
            })}

            <div className="relative w-full sm:w-52 lg:w-56">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-logi-text0" />
              <input
                type="search"
                placeholder="Search trips…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full rounded-lg border border-logi-grid bg-logi-silver/80 py-1.5 pl-8 pr-3 text-xs text-logi-text placeholder:text-logi-text0 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-logi-accent/30"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <colgroup>
            <col className="w-[15%]" />
            <col className="w-[16%]" />
            <col className="w-[34%]" />
            <col className="w-[15%]" />
            <col className="w-[20%]" />
          </colgroup>
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="border-b border-logi-grid/80 text-[10px] uppercase tracking-wider text-logi-text0">
              <th className="px-3 py-2 font-semibold sm:px-4">Trip · Vehicle</th>
              <th className="px-3 py-2 font-semibold sm:px-4">Driver</th>
              <th className="px-3 py-2 font-semibold sm:px-4">Route · Product</th>
              <th className="px-3 py-2 font-semibold sm:px-4">ETA · Status</th>
              <th className="min-w-[140px] px-3 py-2 font-semibold sm:px-4">Progress</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-logi-grid/50">
            {filtered.map((trip) => {
              const isSelected = trip.id === selectedTripId;
              const isDelayed = trip.delayStatus === 'delayed';

              return (
                <tr
                  key={trip.id}
                  onClick={() => onSelectTrip?.(trip.id)}
                  className={`cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-logi-accent/10 ring-1 ring-inset ring-cyan-500/35'
                      : isDelayed
                        ? 'bg-red-500/[0.04] hover:bg-red-500/[0.07]'
                        : 'hover:bg-logi-silver'
                  }`}
                >
                  <td className="px-3 py-2.5 align-top sm:px-4">
                    <p className="font-mono text-xs font-bold text-logi-accent">{trip.id}</p>
                    <p className="mt-0.5 font-mono text-[10px] font-semibold text-black/80">
                      {trip.vehicle}
                    </p>
                  </td>
                  <td className="px-3 py-2.5 align-top text-xs leading-snug text-black/80 sm:px-4">
                    {trip.driver}
                  </td>
                  <td className="px-3 py-2.5 align-top sm:px-4">
                    <span className="inline-flex items-start gap-1 text-xs leading-snug text-logi-muted">
                      <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-logi-text0" />
                      <span>
                        <span className="block break-words">{trip.route}</span>
                        <span className="mt-0.5 block text-[10px] font-medium text-amber-700/90">
                          {trip.product}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td className="px-3 py-2.5 align-top sm:px-4">
                    <p className="font-mono text-xs text-black/80">{trip.eta}</p>
                    <div className="mt-1">
                      <DelayBadge status={trip.delayStatus} />
                    </div>
                  </td>
                  <td className="px-3 py-2.5 align-top sm:px-4">
                    <ProgressBar pct={trip.progress} delayed={isDelayed} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <p className="px-4 py-10 text-center text-sm text-logi-text0">
            No trips match your search or filter.
          </p>
        )}
      </div>
    </section>
  );
}
